<template>
  <component :is="editor?.view ? NodeViewWrapper : 'div'" class="music-sheet-node-view my-2 w-full flex" data-music-sheet-wrapper>
    <div class="relative group w-[90%] max-w-[90%] h-fit">
      <!-- Hover toolbar: drag handle + edit + move/delete (only when editable) -->
      <div
        v-if="editor?.isEditable"
        class="absolute -top-3 left-2 z-20 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200"
      >
        <!-- Drag handle: ProseMirror uses [data-drag-handle] to start node drag -->
        <span
          data-drag-handle
          draggable="true"
          contenteditable="false"
          @dragstart="onMoveDragStart"
          class="music-sheet-drag-handle flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 border border-gray-300 dark:border-gray-600 rounded-lg shadow-md text-xs font-semibold cursor-grab active:cursor-grabbing select-none"
          :title="t('musicSheetNode.dragToMove')"
        >
          <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
            <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
            <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
          </svg>
          <span class="hidden sm:inline">{{ t('musicSheetNode.move') }}</span>
        </span>
        <button
          type="button"
          @click="openModal"
          class="flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 border border-gray-300 dark:border-gray-600 rounded-lg shadow-md text-xs font-semibold hover:shadow-lg transition-all"
          :title="t('musicSheetNode.editFull')"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          <span>{{ t('musicSheetNode.editScore') }}</span>
        </button>
        <!-- Fallback controls: keyboard / touch users can move or delete without drag -->
        <span class="flex items-center gap-0.5 px-1 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-md">
          <button type="button" @click="moveNode(-1)" :title="t('musicSheetNode.moveUp')" class="px-1.5 py-0.5 text-gray-500 hover:text-indigo-600 text-xs font-bold rounded hover:bg-gray-100 dark:hover:bg-gray-700">↑</button>
          <button type="button" @click="moveNode(1)" :title="t('musicSheetNode.moveDown')" class="px-1.5 py-0.5 text-gray-500 hover:text-indigo-600 text-xs font-bold rounded hover:bg-gray-100 dark:hover:bg-gray-700">↓</button>
          <button type="button" @click="deleteNode" :title="t('musicSheetNode.removeScore')" class="px-1.5 py-0.5 text-gray-500 hover:text-red-600 text-xs font-bold rounded hover:bg-red-50 dark:hover:bg-red-900/30">✕</button>
        </span>
      </div>

      <!-- Clean Embedded Document View with Left Controls: Original Design -->
      <div class="flex items-start gap-3 p-1 rounded-lg hover:ring-1 hover:ring-indigo-400/40 transition-all group/container w-full h-fit">
        <!-- Left Action Controls: Play & on mouse over Gridstack-style Drag Handle -->
        <div class="shrink-0 flex flex-col items-center gap-1.5 pl-1 pt-2 select-none">
          <!-- Gridstack-style Drag and Drop Handle: ONLY VISIBLE IN VIEWER MODE (NOT EDITABLE) -->
          <div
            v-if="!editor?.isEditable"
            @pointerdown="handleSplitPointerDown"
            draggable="true"
            @dragstart="handleSplitDragStart"
            @dragend="handleSplitDragEnd"
            class="opacity-0 group-hover:opacity-100 group-hover/container:opacity-100 transition-opacity duration-200 w-7 h-7 rounded-lg bg-white dark:bg-gray-800 hover:bg-indigo-50 dark:hover:bg-indigo-900/60 border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-300 shadow-sm cursor-grab active:cursor-grabbing touch-none select-none"
            :title="t('musicSheetNode.splitDragHint')"
          >
            <!-- 6 dots Gridstack Grip Icon -->
            <svg class="w-4 h-4 fill-current pointer-events-none" viewBox="0 0 24 24">
              <circle cx="8" cy="6" r="1.6" /><circle cx="16" cy="6" r="1.6" />
              <circle cx="8" cy="12" r="1.6" /><circle cx="16" cy="12" r="1.6" />
              <circle cx="8" cy="18" r="1.6" /><circle cx="16" cy="18" r="1.6" />
            </svg>
          </div>

          <!-- Round Play / Stop Button: ALWAYS VISIBLE, clean original design -->
          <button
            type="button"
            @click.stop="togglePlayScore"
            class="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 cursor-pointer"
            :class="isPlaying ? 'bg-amber-500 hover:bg-amber-600 text-white animate-pulse' : 'bg-emerald-600 hover:bg-emerald-700 text-white hover:scale-105'"
            :title="isPlaying ? t('songViewer.pauseScore') : t('songViewer.listenScore')"
          >
            <!-- Stop / Pause Square Icon when playing -->
            <svg v-if="isPlaying" class="w-4 h-4 fill-current" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12" rx="1.5" />
            </svg>
            <!-- Play Triangle Icon when stopped -->
            <svg v-else class="w-4 h-4 fill-current ml-0.5" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </button>
        </div>

        <!-- Embedded MusicSheet SVG -->
        <div
          class="flex-1 cursor-pointer min-w-0 h-fit w-full overflow-x-auto"
          @dblclick="openModal"
          :title="editor?.isEditable ? t('musicSheetNode.doubleClickHint') : ''"
        >
          <MusicSheet
            ref="embeddedMusicSheetRef"
            :key="scoreRenderKey"
            :locale="locale"
            :model-value="node.attrs.notesData || node.attrs.musicXmlData || node.attrs.abcData"
            :title="node.attrs.title"
            :time-signature="node.attrs.timeSignature"
            :key-signature="node.attrs.key"
            :tempo="node.attrs.tempo"
            :clef="node.attrs.clef"
            :wrap-mode="node.attrs.wrapMode || 'wrap'"
            :key-changes="node.attrs.keyChanges || []"
            :time-changes="node.attrs.timeChanges || []"
            :tempo-changes="node.attrs.tempoChanges || []"
            :tempo-ramps="node.attrs.tempoRamps || []"
            :swing="node.attrs.swing || { on: false, amount: 0.67 }"
            :readonly="true"
            :embedded="true"
            :fit-container="true"
            :width-percent="90"
            :zoom="props.zoom || node.attrs.zoom || zoomStore?.state?.zoom || 1"
            @playback-end="isPlaying = false"
            @playback-state="(state) => isPlaying = state"
          />
        </div>
      </div>

      <!-- Full MusicSheet Editor Modal -->
      <teleport to="body">
        <div
          v-if="isModalOpen"
          class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 sm:p-6"
          @keydown.esc="closeModal"
          tabindex="-1"
        >
          <div
            class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150"
            @click.stop
          >
            <!-- Modal Header -->
            <div class="flex items-center justify-between px-5 py-3.5 bg-gray-50 dark:bg-gray-800/90 border-b border-gray-200 dark:border-gray-700">
              <div class="flex items-center gap-2">
                <div class="p-1.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded-lg">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-sm font-bold text-gray-900 dark:text-gray-100">{{ t('musicSheetNode.editorModalTitle') }}</h3>
                  <p class="text-[11px] text-gray-500 dark:text-gray-400">{{ t('musicSheetNode.editorModalSubtitle') }}</p>
                </div>
              </div>

              <!-- Header Actions: Cancel / Apply -->
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  @click="closeModal"
                  class="px-3.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg transition-colors"
                >
                  {{ t('musicSheetNode.cancel') }}
                </button>
                <button
                  type="button"
                  @click="applyChanges"
                  class="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-sm transition-colors"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{{ t('musicSheetNode.applyToDoc') }}</span>
                </button>
              </div>
            </div>

            <!-- Modal Content: Full MusicSheet Interactive Component -->
            <div class="flex-1 overflow-y-auto p-4 bg-gray-100 dark:bg-gray-950 flex flex-col justify-center">
              <MusicSheet
                ref="modalMusicSheetRef"
                :locale="locale"
                :model-value="tempScoreData.notesData || tempScoreData.abcData"
                :title="tempScoreData.title"
                :time-signature="tempScoreData.timeSignature"
                :key-signature="tempScoreData.key"
                :tempo="tempScoreData.tempo"
                :clef="tempScoreData.clef"
                :wrap-mode="tempScoreData.wrapMode || 'wrap'"
                :key-changes="tempScoreData.keyChanges || []"
                :time-changes="tempScoreData.timeChanges || []"
                :tempo-changes="tempScoreData.tempoChanges || []"
                :tempo-ramps="tempScoreData.tempoRamps || []"
                :swing="tempScoreData.swing || { on: false, amount: 0.67 }"
                :readonly="false"
                :embedded="false"
                :zoom="1"
                @change="handleModalScoreChange"
              />
            </div>

            <!-- Modal Footer -->
            <div class="flex items-center justify-between px-5 py-2 bg-gray-50 dark:bg-gray-800/80 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
              <span>{{ t('musicSheetNode.escHintPrefix') }} <kbd class="px-1.5 py-0.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-[10px] font-mono shadow-xs">ESC</kbd> {{ t('musicSheetNode.escHintSuffix') }}</span>
              <span class="text-[11px] text-gray-400" v-html="t('musicSheetNode.applyHint')"></span>
            </div>
          </div>
        </div>
      </teleport>
    </div>
  </component>
</template>

<script setup>
import { ref, reactive, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import { NodeViewWrapper } from '@tiptap/vue-3';
import { NodeSelection } from '@tiptap/pm/state';
import MusicSheet from './MusicSheet.vue';
import zoomStore from '@/store/zoom.store';

const defaultTexts = {
  'musicSheetNode.dragToMove': 'Arrastar para mover a partitura',
  'musicSheetNode.move': 'Mover',
  'musicSheetNode.editFull': 'Editar partitura completa',
  'musicSheetNode.editScore': 'Editar Partitura',
  'musicSheetNode.moveUp': 'Mover para cima',
  'musicSheetNode.moveDown': 'Mover para baixo',
  'musicSheetNode.removeScore': 'Remover partitura',
  'musicSheetNode.splitDragHint': 'Arrastar para dividir tela (lado a lado)',
  'songViewer.pauseScore': 'Pausar partitura',
  'songViewer.listenScore': 'Ouvir partitura',
  'musicSheetNode.doubleClickHint': 'Clique duplo para editar partitura',
  'musicSheetNode.modalTitle': 'Editor de Partitura',
  'musicSheetNode.modalSubtitle': 'Edite as notas, compassos, armadura de clave e configurações da partitura',
  'musicSheetNode.cancel': 'Cancelar',
  'musicSheetNode.applyChanges': 'Aplicar ao Documento',
  'musicSheetNode.escHintPrefix': 'Pressione',
  'musicSheetNode.escHintSuffix': 'para fechar sem salvar',
  'musicSheetNode.applyHint': 'Dica: clique em <strong>Aplicar</strong> para atualizar a cifra'
};

let t = (key) => defaultTexts[key] || key;
let locale = ref('pt-BR');
try {
  const i18n = useI18n();
  if (i18n && i18n.t) {
    t = (key, params) => i18n.t(key, params);
    locale = i18n.locale;
  }
} catch (e) {
  const curLang = localStorage.getItem('language') || 'pt-BR';
  locale = ref(curLang);
}

const props = defineProps({
  node: {
    type: Object,
    required: true
  },
  editor: {
    type: Object,
    default: () => ({ isEditable: false })
  },
  updateAttributes: {
    type: Function,
    default: () => {}
  },
  getPos: {
    type: Function,
    default: undefined
  },
  decorations: {
    type: Object,
    default: () => ({})
  },
  selected: {
    type: Boolean,
    default: false
  },
  extension: {
    type: Object,
    default: () => ({})
  },
  zoom: {
    type: [Number, String],
    default: undefined
  }
});

const isModalOpen = ref(false);
const modalMusicSheetRef = ref(null);
const embeddedMusicSheetRef = ref(null);
const isPlaying = ref(false);
const scoreRenderKey = ref(0);

function togglePlayScore() {
  if (embeddedMusicSheetRef.value) {
    embeddedMusicSheetRef.value.togglePlay();
  }
}

function onMoveDragStart(e) {
  if (props.editor?.view && typeof props.getPos === 'function') {
    const pos = props.getPos();
    if (typeof pos === 'number') {
      try {
        const { state, dispatch } = props.editor.view;
        const sel = NodeSelection.create(state.doc, pos);
        dispatch(state.tr.setSelection(sel));
      } catch (err) {
        console.warn('Error selecting node on dragstart:', err);
      }
    }
  }
  if (e.dataTransfer) {
    e.dataTransfer.setData('text/plain', 'music-sheet-block');
    e.dataTransfer.effectAllowed = 'move';
  }
}

function handleSplitDragStart(e) {
  window.__mytriad_is_dragging = true;
  if (e.dataTransfer) {
    e.dataTransfer.setData('text/plain', 'music-sheet-drag');
    e.dataTransfer.effectAllowed = 'move';
  }
  window.dispatchEvent(new CustomEvent('music-sheet-drag-start'));
}

function handleSplitDragEnd() {
  setTimeout(() => {
    window.__mytriad_is_dragging = false;
  }, 200);
  window.dispatchEvent(new CustomEvent('music-sheet-drag-end'));
}

function handleSplitPointerDown(e) {
  if (e.button !== 0) return;
  e.preventDefault();
  e.stopPropagation();
  window.__mytriad_is_dragging = true;

  const startX = e.clientX;
  const startY = e.clientY;
  let hasMoved = false;

  const onPointerMove = (moveEvt) => {
    const dist = Math.hypot(moveEvt.clientX - startX, moveEvt.clientY - startY);
    if (dist > 3) {
      hasMoved = true;
      window.__mytriad_is_dragging = true;
    }
    window.dispatchEvent(new CustomEvent('music-sheet-pointer-move', {
      detail: { clientX: moveEvt.clientX, clientY: moveEvt.clientY, hasMoved }
    }));
  };

  const onPointerUp = (upEvt) => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
    setTimeout(() => {
      window.__mytriad_is_dragging = false;
    }, 200);

    window.dispatchEvent(new CustomEvent('music-sheet-pointer-end', {
      detail: { clientX: upEvt.clientX, clientY: upEvt.clientY, hasMoved }
    }));
  };

  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerUp);

  window.dispatchEvent(new CustomEvent('music-sheet-pointer-start', {
    detail: { startX, startY, clientX: e.clientX, clientY: e.clientY }
  }));
}


const tempScoreData = reactive({
  notesData: null,
  abcData: '',
  musicXmlData: '',
  title: 'Minha Partitura',
  key: 'C',
  timeSignature: '4/4',
  tempo: 120,
  clef: 'treble',
  wrapMode: 'wrap',
  keyChanges: [],
  timeChanges: [],
  tempoChanges: [],
  tempoRamps: [],
  swing: { on: false, amount: 0.67 }
});

// Staged data emitted from modal changes
let stagedData = null;

function openModal() {
  if (!props.editor.isEditable) return;

  tempScoreData.notesData = props.node.attrs.notesData;
  tempScoreData.abcData = props.node.attrs.abcData || '';
  tempScoreData.musicXmlData = props.node.attrs.musicXmlData || '';
  tempScoreData.title = props.node.attrs.title || 'Minha Partitura';
  tempScoreData.key = props.node.attrs.key || 'C';
  tempScoreData.timeSignature = props.node.attrs.timeSignature || '4/4';
  tempScoreData.tempo = props.node.attrs.tempo || 120;
  tempScoreData.clef = props.node.attrs.clef || 'treble';
  tempScoreData.wrapMode = props.node.attrs.wrapMode || 'wrap';
  tempScoreData.keyChanges = Array.isArray(props.node.attrs.keyChanges) ? [...props.node.attrs.keyChanges] : [];
  tempScoreData.timeChanges = Array.isArray(props.node.attrs.timeChanges) ? [...props.node.attrs.timeChanges] : [];
  tempScoreData.tempoChanges = Array.isArray(props.node.attrs.tempoChanges) ? [...props.node.attrs.tempoChanges] : [];
  tempScoreData.tempoRamps = Array.isArray(props.node.attrs.tempoRamps) ? [...props.node.attrs.tempoRamps] : [];
  tempScoreData.swing = props.node.attrs.swing ? { ...props.node.attrs.swing } : { on: false, amount: 0.67 };

  stagedData = null;
  isModalOpen.value = true;
}

function closeModal() {
  isModalOpen.value = false;
  stagedData = null;
}

function handleModalScoreChange(data) {
  stagedData = data;
}

function applyChanges() {
  const currentData = modalMusicSheetRef.value?.getScoreData?.() || stagedData;
  if (currentData) {
    const updatedAttrs = {
      notesData: currentData.voices && currentData.voices.length > 0 ? currentData.voices : (currentData.notes || null),
      abcData: currentData.abc || '',
      musicXmlData: currentData.musicXml || '',
      title: currentData.title || props.node.attrs.title || 'Partitura',
      key: currentData.key || props.node.attrs.key || 'C',
      timeSignature: currentData.timeSignature || props.node.attrs.timeSignature || '4/4',
      tempo: currentData.tempo || props.node.attrs.tempo || 120,
      clef: currentData.clef || props.node.attrs.clef || 'treble',
      wrapMode: currentData.wrapMode || props.node.attrs.wrapMode || 'wrap',
      keyChanges: currentData.keyChanges || [],
      timeChanges: currentData.timeChanges || [],
      tempoChanges: currentData.tempoChanges || [],
      tempoRamps: currentData.tempoRamps || [],
      swing: currentData.swing || { on: false, amount: 0.67 }
    };
    props.updateAttributes(updatedAttrs);
    scoreRenderKey.value++;
    // Force editor transaction docChanged so SongEditor emits updated modelValue
    if (props.editor) {
      try {
        props.editor.view.dispatch(props.editor.state.tr);
      } catch (e) {
        // ignore dispatch errors
      }
    }
  }
  closeModal();
}

// --- Drag & drop helpers -------------------------------------------------
// The node itself is draggable (see extension: atom + draggable). ProseMirror
// starts the native drag from the [data-drag-handle] element above. The
// buttons below are a fallback for keyboard / touch users.
function moveNode(direction) {
  const getPos = props.getPos;
  if (typeof getPos !== 'function') return;
  const pos = getPos();
  if (pos == null) return;
  const { state } = props.editor;
  const node = state.doc.nodeAt(pos);
  if (!node) return;
  const $pos = state.doc.resolve(pos);
  // Move within the parent block container by swapping with the sibling node.
  const parent = $pos.parent;
  const indexInParent = $pos.index();
  const targetIndex = indexInParent + direction;
  if (targetIndex < 0 || targetIndex >= parent.childCount) return;
  const targetChild = parent.child(targetIndex);
  // Only swap with block-level siblings (avoid breaking inline content).
  if (!targetChild.type.isBlock) return;
  let tr = state.tr;
  const nodeSize = node.nodeSize;
  const targetSize = targetChild.nodeSize;
  if (direction < 0) {
    // target is before current node
    const targetPos = pos - targetSize;
    tr = tr.replaceWith(targetPos, targetPos + targetSize + nodeSize, [node, targetChild]);
  } else {
    tr = tr.replaceWith(pos, pos + nodeSize + targetSize, [targetChild, node]);
  }
  props.editor.view.dispatch(tr);
  props.editor.commands.focus();
}

function deleteNode() {
  const getPos = props.getPos;
  if (typeof getPos !== 'function') {
    props.editor.commands.deleteSelection();
    return;
  }
  const pos = getPos();
  if (pos == null) return;
  const { state } = props.editor;
  const node = state.doc.nodeAt(pos);
  if (!node) return;
  const tr = state.tr.delete(pos, pos + node.nodeSize);
  props.editor.view.dispatch(tr);
}
</script>

<style scoped>
.music-sheet-node-view {
  display: block;
}

/* Drag handle affordance */
.music-sheet-drag-handle {
  touch-action: none;
  -webkit-user-drag: element;
}

/* Selected-node feedback (ProseMirror adds .ProseMirror-selectednode on the wrapper) */
.music-sheet-node-view.ProseMirror-selectednode .group\/container {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
  border-radius: 8px;
}
</style>
